/**
 * Reading the recorded GPS track off `visit.meta` — pure and dependency-free, so `yarn check:gps`
 * runs it under node the way `check:tracking` runs `tracking/derive.js`.
 *
 * The mobile app (`sendLog` in picsell_api.dart) PATCHes the visit at close with
 *   { visitWithoutGeoData: bool, logs: "<JSON STRING of LocationLog[]>" }
 * where a LocationLog is { latitude, longitude (STRINGS), dateTime (device-local
 * "YYYY-MM-DD HH:mm:ss.ffffff", no zone), gpsEnabled, permissionsGranted, isFake }.
 * `meta` arrives only from `VisitDetailSerializer`; older rows and other writers may hold a bare
 * list, so every plausible shape is accepted rather than assumed.
 *
 * ⚠ A FAILED fix is written as latitude = longitude = -1 (`TrySetLocationAction`, whenever the
 * service is off, permission is denied, there is no connection, or the fix itself fails). That is a
 * real coordinate in the Gulf of Guinea, not an absence, so it is dropped here.
 */

/** A ping the mobile app marked as coming from a mock-location provider (`isFake`, from Android's
    isMocked). A flagged ping turns its dot red and raises the section's warning (PICSELL-1581 #5). */
export const isMockPing = ping => ping.mock === true || ping.mock === 1 || ping.mock === "true";

export function pingsOf(visit) {
  return parseMeta(visit?.meta)
    .map(entry => {
      if (Array.isArray(entry) && entry.length >= 2) return { pos: [coord(entry[0]), coord(entry[1])] };
      if (entry && typeof entry === "object") {
        const lat = entry.latitude ?? entry.lat;
        const lng = entry.longitude ?? entry.lng ?? entry.lon;
        if (lat !== undefined && lng !== undefined) {
          return {
            pos: [coord(lat), coord(lng)],
            mock: entry.isFake ?? entry.mock ?? entry.is_mock ?? entry.isFromMockProvider ?? entry.is_from_mock_provider,
            time: isoTime(entry.dateTime ?? entry.time ?? entry.timestamp ?? entry.created ?? entry.date),
          };
        }
      }
      return null;
    })
    .filter(ping => ping && Number.isFinite(ping.pos[0]) && Number.isFinite(ping.pos[1]) && !(ping.pos[0] === -1 && ping.pos[1] === -1));
}

/** Great-circle distance in metres (haversine) — a ping is compared against the visit's point, so
    the distances are hundreds of metres to a few km, well inside the formula's accuracy. */
export function distanceM([lat1, lng1], [lat2, lng2]) {
  const rad = deg => (deg * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(a));
}

/** "120 m" under a kilometre, "1.2 km" above — the unit words come from the caller's dictionary. */
export const formatDistance = (metres, units, locale) =>
  metres < 1000 ? `${Math.round(metres)} ${units.m}` : `${(metres / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} ${units.km}`;

/** `Number(null)` and `Number("")` are both 0 — the equator, not "missing". */
const coord = value => (value === null || value === "" ? NaN : Number(value));

/** Dart's `DateTime.now().toString()` is "2026-09-09 12:42:12.664627"; Safari rejects the space and
    a fraction longer than three digits, so hand `Date` the ISO form. It carries no zone, so it reads
    as the viewer's local time — the merchandiser's zone in every case this app serves. */
const isoTime = value => (typeof value === "string" ? value.replace(" ", "T").replace(/(\.\d{3})\d+/, "$1") : value);

/** A JSONField can hand back a string rather than a list, and the current mobile app wraps the
    list — itself JSON-encoded — in `{ logs }`; unwrap all of it or draw nothing silently. */
export function parseMeta(meta) {
  if (typeof meta === "string" && meta) {
    try {
      meta = JSON.parse(meta);
    } catch {
      return [];
    }
  }
  if (Array.isArray(meta)) return meta;
  if (meta && typeof meta === "object" && "logs" in meta) return parseMeta(meta.logs);
  return [];
}
